// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import type { TyApiResponse } from "@/types";
// --- Logic --------------------------------------------------------------------------------------
/**
 * **The one exception to "`useFetch` is the only way to call the API".**
 *
 * Two things force it. `fetch` cannot report upload progress — there is no event for it, and no
 * workaround that holds across Safari — so a progress bar means `XMLHttpRequest`. And uploads have to
 * outlive the screen that started them, so they cannot hang off a hook that unmounts with it; these
 * read the stores directly instead, which works from anywhere, including after the user has navigated
 * away.
 *
 * Everything else in the app still goes through `useFetch`.
 */
const headers = (): Record<string, string> => {
    const token = useAuth.getState().token;
    const organizationId = useOrganization.getState().organizationId;

    const built: Record<string, string> = { "x-language": useLang.getState().lang ?? "ar" };
    if (token) built["Authorization"] = `Bearer ${token}`;
    if (organizationId) built["x-organization-id"] = organizationId;
    return built;
};

/** A call to our own API, outside React. Throws the server's own translated message on failure. */
export async function callApi<T>(path: string, method: "POST" | "DELETE", body?: unknown): Promise<T> {
    const response = await fetch(`${constants.url}${path}`, {
        method,
        headers: { ...headers(), ...(body ? { "Content-Type": "application/json" } : {}) },
        body: body ? JSON.stringify(body) : undefined,
    });

    const payload = (await response.json()) as TyApiResponse<T>;
    if (!response.ok || !payload.success) throw new Error(payload.message);
    return payload.data as T;
}

export type TyPutHandle = { done: Promise<void>; abort: () => void };

/**
 * The bytes, straight into S3. No `Authorization` header and no cookies — the presigned URL carries
 * its own signature, and sending ours would break it.
 *
 * Returns the abort alongside the promise, because cancelling a half-sent 200 MB video is the whole
 * point of showing a bar in the first place.
 */
export function putToStorage(url: string, body: Blob, onProgress?: (percent: number) => void): TyPutHandle {
    const request = new XMLHttpRequest();

    const done = new Promise<void>((resolve, reject) => {
        request.open("PUT", url, true);
        request.setRequestHeader("Content-Type", body.type || "application/octet-stream");

        request.upload.onprogress = (event) => {
            if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
        };

        request.onload = () => (request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`storage responded ${request.status}`)));
        request.onerror = () => reject(new Error("network"));
        request.onabort = () => reject(new Error("aborted"));

        request.send(body);
    });

    return { done, abort: () => request.abort() };
}
